import type { ChatMessageAttachment } from "@albertine/types/openapi/concierge-v1";
import { fileToChatAttachment, mimeTypeFromFileHeader } from "./fileToChatAttachment";

// Canonical extension first; keep in sync with api-server allowlist.
const UPLOAD_FILE_EXTENSIONS: Record<string, readonly string[]> = {
    "image/png": ["png"],
    "image/jpeg": ["jpg", "jpeg"],
    "audio/webm": ["webm"],
    "audio/mp4": ["m4a"],
    "audio/ogg": ["ogg"],
};

export const ACCEPTED_CHAT_FILE_TYPES = ["image/png", "image/jpeg"] as const;

export type ChatUploadDescriptor = {
    localId: string;
    fileName: string;
    mimeType: string;
    fileUploadId?: string;
};

export type PendingChatUpload = {
    attachment: Extract<ChatMessageAttachment, { type: "file" }>;
    file: File;
    upload: ChatUploadDescriptor;
};

const SNIFF_BYTE_COUNT = 16;

/**
 * Copy the picked file into a new in-memory File before preview or upload.
 *
 * Android WebView file inputs, and Files cloned into the chat iframe with
 * postMessage, can only be read once. The preview image otherwise consumes
 * the bytes and the upload sends an empty body, which the server rejects.
 * Magic bytes win over a contradictory File.type so a screenshot labeled
 * image/png but encoded as JPEG still finalizes.
 */
export async function materializeChatUploadFile(file: File): Promise<File> {
    const bytes = await readFileBytes(file);
    const header = new Uint8Array(bytes.slice(0, SNIFF_BYTE_COUNT));
    const sniffed = mimeTypeFromFileHeader(header);
    const declared =
        file.type && file.type !== "application/octet-stream" ? file.type : null;
    const mimeType = normalizeUploadMimeType(
        sniffed ?? declared ?? "application/octet-stream",
    );
    const fileName = normalizeUploadFileName(file.name || "attachment", mimeType);

    return new File([bytes], fileName, { type: mimeType });
}

export async function createPendingChatUpload(file: File): Promise<PendingChatUpload> {
    const stableFile = await materializeChatUploadFile(file);
    const attachment = await fileToChatAttachment(stableFile);
    const mimeType = normalizeUploadMimeType(attachment.mimeType);
    const localId = crypto.randomUUID();

    return {
        attachment,
        file: stableFile,
        upload: {
            localId,
            fileName: normalizeUploadFileName(attachment.filename, mimeType),
            mimeType,
        },
    };
}

function readFileBytes(file: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result as ArrayBuffer);
        };
        reader.onerror = () => {
            reject(reader.error ?? new Error("Failed to read file"));
        };
        reader.readAsArrayBuffer(file);
    });
}

export function isSupportedChatUploadMimeType(mimeType: string): boolean {
    return normalizeUploadMimeType(mimeType) in UPLOAD_FILE_EXTENSIONS;
}

export function normalizeUploadMimeType(mimeType: string): string {
    return mimeType.split(";")[0].trim().toLowerCase();
}

export function normalizeUploadFileName(fileName: string, mimeType: string): string {
    const allowedExtensions = UPLOAD_FILE_EXTENSIONS[normalizeUploadMimeType(mimeType)];
    if (!allowedExtensions) {
        return fileName;
    }

    const extension = fileExtensionOf(fileName);
    if (extension && allowedExtensions.includes(extension)) {
        return fileName;
    }

    return `${fileName}.${allowedExtensions[0]}`;
}

function fileExtensionOf(fileName: string): string | null {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
        return null;
    }

    return fileName.slice(lastDotIndex + 1).toLowerCase();
}
